export const DEFAULT_USER_STATS = {
  total: 0,
  admin: 0,
  judoStarter: 0,
  judoExpert: 0,
  coach: 0,
  coachPro: 0,
  coachUltim: 0,
};

export const DEFAULT_EVENT_STATS = {
  events_count: 0,
  shared_events_count: 0,
};

export const DEFAULT_GROUP_STATS = {
  count: 0,
  groups_count: 0,
};

export const DEFAULT_ABONNEMENT_STATS = {
  total: 0,
  detail: {},
};

export function formatCurrency(value) {
  const amount = Number(value || 0);

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatCompactNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(Number(value || 0));
}

export function formatShortDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
}

export function normalizeUserStats(response) {
  return {
    ...DEFAULT_USER_STATS,
    ...(response || {}),
  };
}

export function normalizeEventStats(response) {
  return {
    ...DEFAULT_EVENT_STATS,
    ...(response || {}),
  };
}

export function normalizeGroupStats(response) {
  return {
    ...DEFAULT_GROUP_STATS,
    ...(response || {}),
    count: response?.count || response?.groups_count || 0,
  };
}

export function normalizeAbonnementStats(response) {
  return {
    ...DEFAULT_ABONNEMENT_STATS,
    ...(response || {}),
    detail: response?.detail || {},
  };
}

export function normalizeMessages(messages = []) {
  return [...messages].sort((a, b) => {
    if (Boolean(a.read_message) !== Boolean(b.read_message)) {
      return a.read_message ? 1 : -1;
    }

    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
}

export function prepareVisitsData(visits = []) {
  return [...visits]
    .map((visit) => ({
      ...visit,
      date_label: formatShortDate(visit.date_visite),
      compteur: Number(visit.compteur || 0),
    }))
    .sort((a, b) => new Date(a.date_visite) - new Date(b.date_visite));
}

export function prepareAdherentsGrowthData(adherents = []) {
  const byDate = adherents.reduce((acc, adherent) => {
    const sourceDate = adherent.createdAt || adherent.created_at;

    if (!sourceDate) return acc;

    const date = new Date(sourceDate);

    if (Number.isNaN(date.getTime())) return acc;

    const key = date.toISOString().split("T")[0];

    acc[key] = (acc[key] || 0) + 1;

    return acc;
  }, {});

  let cumulative = 0;

  return Object.entries(byDate)
    .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
    .map(([date, count]) => {
      cumulative += count;

      return {
        date,
        date_label: formatShortDate(date),
        count: cumulative,
        nouveaux: count,
      };
    });
}

export function prepareDepartmentData(adherents = [], departements = []) {
  const counts = adherents.reduce((acc, adherent) => {
    const code = adherent.departement || "Inconnu";
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .map(([code, count]) => {
      const deptInfo = departements.find((dept) => String(dept.code) === String(code));

      return {
        code,
        departement: deptInfo?.departement || "Inconnu",
        region: deptInfo?.region || "Non spécifiée",
        count,
      };
    })
    .sort((a, b) => {
      if (a.code === "Inconnu") return 1;
      if (b.code === "Inconnu") return -1;

      return Number(a.code) - Number(b.code);
    });
}

export function prepareSubscriptionsData(abonnementStats) {
  return Object.entries(abonnementStats?.detail || {}).map(([name, value]) => ({
    name,
    value: Number(value || 0),
  }));
}

export function getUnreadMessagesCount(messages = []) {
  return messages.filter((message) => !message.read_message).length;
}

export function getLatestMessages(messages = [], limit = 8) {
  return normalizeMessages(messages).slice(0, limit);
}

export function buildDashboardKpis({
  userStats,
  eventStats,
  groupStats,
  abonnementStats,
  messages,
  adherents,
}) {
  return {
    totalUsers: Number(userStats.total || 0),
    totalAdherents: Array.isArray(adherents) ? adherents.length : 0,
    totalEvents: Number(eventStats.events_count || 0),
    sharedEvents: Number(eventStats.shared_events_count || 0),
    totalGroups: Number(groupStats.count || groupStats.groups_count || 0),
    subscriptionTotal: Number(abonnementStats.total || 0),
    unreadMessages: getUnreadMessagesCount(messages),
    coachTotal:
      Number(userStats.coach || 0) +
      Number(userStats.coachPro || 0) +
      Number(userStats.coachUltim || 0),
  };
}